import * as React from 'react';
import { Avatar, Button, Drawer, Empty, Form, Input, InputNumber, Select, Space, Spin, Tabs, Typography } from 'antd';
import { CloseOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';
import { useCampaignPosts } from '../queryHooks';
import type { CampaignCreator } from '../types';
import PostCard from './PostCard';

export type CreatorPanelMeta = {
    budget: number | null;
    comments: string;
    commission: number | null;
    paid_amount: number | null;
    payment_status: string;
    contract_url: string;
    contract_status: string;
    po_number: string;
};

const CONTRACT_STATUS = [
    { label: 'Not set', value: '' },
    { label: 'Draft', value: 'draft' },
    { label: 'Sent', value: 'sent' },
    { label: 'Signed', value: 'signed' },
    { label: 'Declined', value: 'declined' },
];

const PAYMENT_STATUS = [
    { label: 'Unpaid', value: 'unpaid' },
    { label: 'Partial', value: 'partial' },
    { label: 'Paid', value: 'paid' },
];

type Props = {
    campaignId: number;
    creators: CampaignCreator[];
    creatorId: number;
    meta: CreatorPanelMeta;
    deliverables: React.ReactNode;
    saving?: boolean;
    avatar?: string;
    onClose: () => void;
    onChangeCreator: (id: number) => void;
    onViewProfile: () => void;
    onSave: (patch: Partial<CreatorPanelMeta>) => Promise<void>;
};

const CreatorPanel: React.FC<Props> = ({
    campaignId, creators, creatorId, meta, deliverables, saving, avatar,
    onClose, onChangeCreator, onViewProfile, onSave,
}) => {
    const index = creators.findIndex((c) => c.id === creatorId);
    const creator = creators[index];
    const { data: posts = [], isLoading: postsLoading } = useCampaignPosts(campaignId, {
        status: 'published',
        creator_id: String(creatorId),
    });

    const [costs, setCosts] = React.useState(meta);
    const [notes, setNotes] = React.useState(meta.comments);
    const [contract, setContract] = React.useState({
        contract_url: meta.contract_url,
        contract_status: meta.contract_status,
        po_number: meta.po_number,
    });

    React.useEffect(() => {
        setCosts(meta);
        setNotes(meta.comments);
        setContract({
            contract_url: meta.contract_url,
            contract_status: meta.contract_status,
            po_number: meta.po_number,
        });
    }, [creatorId, meta]);

    if (!creator) return null;

    const avatarSrc = avatar || creator.accounts?.find((a) => a.image)?.image;
    const name = creator.creator || `Creator #${creator.id}`;
    const prev = index > 0 ? creators[index - 1] : undefined;
    const next = index >= 0 && index < creators.length - 1 ? creators[index + 1] : undefined;

    return (
        <Drawer
            open
            width={680}
            closable={false}
            onClose={onClose}
            bodyStyle={{ padding: 0, background: 'var(--surface)' }}
            headerStyle={{ display: 'none' }}
        >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', borderBottom: '1px solid var(--surface-border)' }}>
                <Button type="text" icon={<CloseOutlined />} onClick={onClose} aria-label="Close" />
                <Space size={4}>
                    <Button type="text" icon={<LeftOutlined />} disabled={!prev} onClick={() => prev && onChangeCreator(prev.id)}>
                        Previous
                    </Button>
                    <Button type="text" disabled={!next} onClick={() => next && onChangeCreator(next.id)}>
                        Next <RightOutlined />
                    </Button>
                </Space>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '18px 24px', borderBottom: '1px solid var(--surface-border)' }}>
                <Avatar size={52} src={avatarSrc ? `data:image/jpeg;base64,${avatarSrc}` : undefined} style={{ background: '#d3adf7', flexShrink: 0 }}>
                    {!avatarSrc && (name[0] ?? '?').toUpperCase()}
                </Avatar>
                <Typography.Title level={4} style={{ margin: 0, flex: 1, fontSize: 22 }} ellipsis>
                    {name}
                </Typography.Title>
                <Button onClick={onViewProfile}>View Profile</Button>
            </div>
            <Tabs
                tabBarStyle={{ padding: '0 24px', marginBottom: 0, fontSize: 15 }}
                items={[
                    {
                        key: 'content',
                        label: 'Content',
                        children: (
                            <div style={{ padding: 24 }}>
                                {postsLoading ? <div style={{ textAlign: 'center', padding: 32 }}><Spin /></div>
                                    : posts.length === 0
                                        ? <Empty description="No posts in this campaign yet" />
                                        : (
                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                                                {posts.map((p) => <PostCard key={p.id} post={p} creatorName={name} />)}
                                            </div>
                                        )}
                            </div>
                        ),
                    },
                    {
                        key: 'costs',
                        label: 'Costs',
                        children: (
                            <Form layout="vertical" style={{ padding: 24, maxWidth: 420 }} requiredMark={false}>
                                <Form.Item label="Fee">
                                    <InputNumber
                                        style={{ width: '100%' }}
                                        prefix="€"
                                        min={0}
                                        value={costs.budget ?? undefined}
                                        onChange={(v) => setCosts((s) => ({ ...s, budget: v ?? null }))}
                                    />
                                </Form.Item>
                                <Form.Item label="Commission">
                                    <InputNumber
                                        style={{ width: '100%' }}
                                        prefix="€"
                                        min={0}
                                        value={costs.commission ?? undefined}
                                        onChange={(v) => setCosts((s) => ({ ...s, commission: v ?? null }))}
                                    />
                                </Form.Item>
                                <Form.Item label="Paid">
                                    <InputNumber
                                        style={{ width: '100%' }}
                                        prefix="€"
                                        min={0}
                                        value={costs.paid_amount ?? undefined}
                                        onChange={(v) => setCosts((s) => ({ ...s, paid_amount: v ?? null }))}
                                    />
                                </Form.Item>
                                <Form.Item label="Payment status">
                                    <Select
                                        value={costs.payment_status || 'unpaid'}
                                        options={PAYMENT_STATUS}
                                        onChange={(v) => setCosts((s) => ({ ...s, payment_status: v }))}
                                    />
                                </Form.Item>
                                <Button type="primary" loading={saving} onClick={() => onSave({
                                    budget: costs.budget,
                                    commission: costs.commission,
                                    paid_amount: costs.paid_amount,
                                    payment_status: costs.payment_status || 'unpaid',
                                })}>
                                    Save
                                </Button>
                            </Form>
                        ),
                    },
                    {
                        key: 'deliverables',
                        label: 'Deliverables',
                        children: <div style={{ padding: 24 }}>{deliverables}</div>,
                    },
                    {
                        key: 'contract',
                        label: 'Contract',
                        children: (
                            <Form layout="vertical" style={{ padding: 24, maxWidth: 480 }} requiredMark={false}>
                                <Form.Item label="URL">
                                    <Input
                                        value={contract.contract_url}
                                        placeholder="https://"
                                        onChange={(e) => setContract((s) => ({ ...s, contract_url: e.target.value }))}
                                    />
                                </Form.Item>
                                <Form.Item label="Status">
                                    <Select
                                        value={contract.contract_status}
                                        options={CONTRACT_STATUS}
                                        onChange={(v) => setContract((s) => ({ ...s, contract_status: v }))}
                                    />
                                </Form.Item>
                                <Form.Item label="PO number">
                                    <Input
                                        value={contract.po_number}
                                        onChange={(e) => setContract((s) => ({ ...s, po_number: e.target.value }))}
                                    />
                                </Form.Item>
                                <Button type="primary" loading={saving} onClick={() => onSave(contract)}>
                                    Save
                                </Button>
                            </Form>
                        ),
                    },
                    {
                        key: 'notes',
                        label: 'Notes',
                        children: (
                            <div style={{ padding: 24, maxWidth: 560 }}>
                                <Input.TextArea
                                    value={notes}
                                    autoSize={{ minRows: 5, maxRows: 12 }}
                                    placeholder="Type a note for this creator in the campaign"
                                    style={{ fontSize: 15 }}
                                    onChange={(e) => setNotes(e.target.value)}
                                />
                                <Button type="primary" style={{ marginTop: 12 }} loading={saving} onClick={() => onSave({ comments: notes })}>
                                    Save
                                </Button>
                            </div>
                        ),
                    },
                ]}
            />
        </Drawer>
    );
};

export default CreatorPanel;
